<template>
  <ul class="utterance-list">
    <transition-group name="fade">
      <UtteranceListItem
        v-for="utterance in utterances"
        :key="utterance.id"
        v-model:utterance="utterances[utterances.indexOf(utterance)]"
        :all-utterances="utterances"
        :term="term"
        :is-editable="isEditable"
        @delete="deleteUtterance"
        @move-to-next="moveToNextUtterance"
        ref="utteranceItems"
      />
    </transition-group>

    <li class="utterance-list-item add-item-wrapper">
      <p class="help is-danger" v-if="isUtteranceRequiredInvalid">You must add at least one {{ term }}</p>
      <button
        class="button is-primary is-outlined is-fullwidth add-utterance"
        @click="addUtterance()"
        :disabled="!isEditable"
      >
        Add Another Example
      </button>
    </li>
  </ul>
</template>

<script lang="ts" setup>
import { UtteranceViewModel } from '@/features/bots/models/intents/utteranceViewModel';
import { nanoid } from 'nanoid';
import { computed, nextTick, onMounted, PropType, ref } from 'vue';
import { useVModel } from '@vueuse/core';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import UtteranceListItem from './UtteranceListItem.vue';
import VuelidateUtil from '@shared/util/vuelidateUtil';

const props = defineProps({
  modelValue: {
    type: Array as PropType<UtteranceViewModel[]>,
    required: true,
  },
  term: {
    type: String,
    default: 'utterance',
  },
  isEditable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: UtteranceViewModel[]): void;
}>();

const utterances = useVModel(props, 'modelValue', emit);
const utteranceItems = ref<InstanceType<typeof UtteranceListItem>[]>([]);

const validations = {
  utterances: {
    required,
  },
};

const $v = useVuelidate(validations, { utterances });
const isUtteranceRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, 'utterances'));

onMounted(() => {
  if ($v.value.$reset) {
    $v.value.$reset();
  }

  if (utterances.value.length === 0) {
    addUtterance();
  }
});

function addUtterance() {
  const utteranceId = nanoid(7);
  utterances.value.push({
    id: utteranceId,
    text: '',
  });
  focusOnUtterance(utteranceId);
}

function deleteUtterance(id: string) {
  $v.value.$touch();
  utterances.value = utterances.value.filter((u) => u.id !== id);
}

function moveToNextUtterance(fromUtteranceId: string) {
  const currentIndex = utterances.value.findIndex((u) => u.id === fromUtteranceId);
  const nextIndex = currentIndex + 1;
  if (nextIndex >= utterances.value.length) {
    addUtterance();
  } else {
    const nextUtteranceId = utterances.value[nextIndex].id;
    focusOnUtterance(nextUtteranceId);
  }
}

function focusOnUtterance(utteranceId: string) {
  nextTick(() => {
    const index = utterances.value.findIndex((u) => u.id === utteranceId);
    if (utteranceItems.value[index]) {
      utteranceItems.value[index].focus();
    }
  });
}
</script>

<style scoped lang="scss">
.add-item-wrapper {
  &.utterance-list-item {
    padding: 0.25rem 0.5rem;
  }

  button {
    height: 2.75em;
  }

  p.help {
    margin: 0.5rem 0;
  }
}
</style>
